/* =====================================================================
   OVAT — animaciones e interacciones
   ===================================================================== */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = window.matchMedia('(hover: hover)').matches;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  window.ovatReady = true;

  /* -------------------------------------------------------------------
     Aparición al hacer scroll
     ------------------------------------------------------------------- */
  // Los elementos recortados con clip-path no se detectan como visibles,
  // así que para las imágenes se observa su contenedor.
  const revealTargets = new Map();

  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            let order = 0;
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const elements = revealTargets.get(entry.target) || [];
              elements.forEach((el) => {
                if (el.hasAttribute('data-ovat-cascade')) el.style.setProperty('--d', order++);
                el.classList.add('is-inview');
              });
              revealTargets.delete(entry.target);
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -6% 0px', threshold: 0.04 }
        )
      : null;

  function initReveal(scope = document) {
    const elements = scope.querySelectorAll('[data-ovat-reveal]:not(.is-inview)');
    const animate = root.classList.contains('ovat-anim') && revealObserver;

    elements.forEach((el) => {
      if (el.closest('[data-ovat-manual]')) return;
      if (!animate) {
        el.classList.add('is-inview');
        return;
      }
      const target = el.dataset.ovatReveal === 'image' && el.parentElement ? el.parentElement : el;
      const list = revealTargets.get(target);
      if (list) {
        if (!list.includes(el)) list.push(el);
        return;
      }
      revealTargets.set(target, [el]);
      revealObserver.observe(target);
    });
  }

  /* -------------------------------------------------------------------
     Scroll: header transparente, parallax y botón volver arriba
     ------------------------------------------------------------------- */
  let parallaxItems = [];
  const backToTop = () => document.querySelector('.ovat-top');

  function collectParallax() {
    parallaxItems = reduceMotion ? [] : [...document.querySelectorAll('[data-ovat-parallax]')];
  }

  function setHeaderHeight() {
    const header = document.querySelector('.section-header');
    if (header) root.style.setProperty('--header-height', `${header.offsetHeight}px`);
  }

  function onScrollFrame() {
    const y = window.scrollY;
    const vh = window.innerHeight;

    document.body.classList.toggle('ovat-at-top', y < 8);

    parallaxItems.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > vh + 200) return;
      const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
      const amount = parseFloat(el.dataset.ovatParallax) || 10;
      el.style.setProperty('--ovat-py', `${(-progress * amount * rect.height) / 100}px`);
    });

    const topButton = backToTop();
    if (topButton) {
      const max = document.documentElement.scrollHeight - vh;
      topButton.style.setProperty('--p', max > 0 ? Math.min(1, y / max).toFixed(3) : 0);
      topButton.classList.toggle('is-visible', y > vh * 0.8);
    }
  }

  let scrollQueued = false;
  function requestScrollFrame() {
    if (scrollQueued) return;
    scrollQueued = true;
    requestAnimationFrame(() => {
      scrollQueued = false;
      onScrollFrame();
    });
  }

  window.addEventListener('scroll', requestScrollFrame, { passive: true });
  window.addEventListener('resize', () => {
    setHeaderHeight();
    requestScrollFrame();
  });

  /* -------------------------------------------------------------------
     Panel lateral genérico (guía de tallas)
     ------------------------------------------------------------------- */
  function openDrawer(id, trigger) {
    const candidates = [...document.querySelectorAll('.ovat-drawer')].filter((el) => el.id === id);
    const drawer = candidates.find((el) => el.parentElement !== document.body) || candidates[0];
    if (!drawer) return;

    // Se mueve al body para que ningún contenedor con transform lo recorte,
    // excepto dentro de la compra rápida (que atrapa el foco).
    if (!drawer.closest('quick-add-modal') && drawer.parentElement !== document.body) {
      candidates.filter((el) => el !== drawer && el.parentElement === document.body).forEach((el) => el.remove());
      document.body.appendChild(drawer);
    }

    drawer.ovatTrigger = trigger;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    root.classList.add('ovat-lock');
    if (!drawer.closest('quick-add-modal')) {
      setTimeout(() => drawer.querySelector('.ovat-drawer__panel')?.focus({ preventScroll: true }), 60);
    }
  }

  function closeDrawer(drawer) {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.ovat-drawer.is-open')) root.classList.remove('ovat-lock');
    drawer.ovatTrigger?.focus?.({ preventScroll: true });
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-ovat-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(opener.dataset.ovatDrawerOpen, opener);
      return;
    }
    const closer = event.target.closest('[data-ovat-drawer-close]');
    if (closer) closeDrawer(closer.closest('.ovat-drawer'));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.ovat-drawer.is-open').forEach(closeDrawer);
  });

  document.addEventListener('click', (event) => {
    const behavior = reduceMotion ? 'auto' : 'smooth';

    if (event.target.closest('.ovat-top')) {
      window.scrollTo({ top: 0, behavior });
      return;
    }

    const scrollNext = event.target.closest('[data-ovat-scroll-next]');
    if (scrollNext) {
      const next = scrollNext.closest('.shopify-section')?.nextElementSibling;
      if (!next) return;
      const offset = document.querySelector('.section-header')?.offsetHeight || 0;
      window.scrollTo({ top: next.getBoundingClientRect().top + window.scrollY - offset, behavior });
    }
  });

  /* -------------------------------------------------------------------
     Carrusel horizontal + pestañas  <ovat-slider>
     ------------------------------------------------------------------- */
  function enableDrag(track) {
    let pointerDown = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      pointerDown = true;
      moved = false;
      startX = event.clientX;
      startLeft = track.scrollLeft;
    });

    window.addEventListener('pointermove', (event) => {
      if (!pointerDown) return;
      const dx = event.clientX - startX;
      if (!moved && Math.abs(dx) > 6) {
        moved = true;
        track.classList.add('is-dragging');
      }
      if (moved) track.scrollLeft = startLeft - dx;
    });

    window.addEventListener('pointerup', () => {
      if (!pointerDown) return;
      pointerDown = false;
      if (moved) requestAnimationFrame(() => track.classList.remove('is-dragging'));
    });

    track.addEventListener(
      'click',
      (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      },
      true
    );

    track.addEventListener('dragstart', (event) => event.preventDefault());
  }

  class OvatSlider extends HTMLElement {
    connectedCallback() {
      this.prevButton = this.querySelector('[data-ovat-prev]');
      this.nextButton = this.querySelector('[data-ovat-next]');
      this.update = this.update.bind(this);

      this.prevButton?.addEventListener('click', () => this.go(-1));
      this.nextButton?.addEventListener('click', () => this.go(1));

      this.querySelectorAll('.ovat-track').forEach((track) => {
        track.addEventListener('scroll', this.update, { passive: true });
        enableDrag(track);
      });

      this.tabs = [...this.querySelectorAll('[data-ovat-tab]')];
      this.panels = [...this.querySelectorAll('[data-ovat-panel]')];
      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.selectTab(tab.dataset.ovatTab)));

      window.addEventListener('resize', this.update);
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.update);
    }

    get track() {
      return this.querySelector('[data-ovat-panel]:not([hidden]) .ovat-track') || this.querySelector('.ovat-track');
    }

    go(direction) {
      const track = this.track;
      if (!track) return;
      const first = track.children[0];
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const step = first ? first.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
      track.scrollBy({ left: direction * step, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    update() {
      const track = this.track;
      if (!track) return;
      const max = track.scrollWidth - track.clientWidth;
      if (this.prevButton) this.prevButton.disabled = track.scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = track.scrollLeft >= max - 2;

      const bar = this.querySelector('.ovat-progress span');
      if (bar) {
        const visible = track.scrollWidth ? Math.min(1, track.clientWidth / track.scrollWidth) : 1;
        const progress = max > 0 ? track.scrollLeft / max : 0;
        bar.style.setProperty('--w', `${visible * 100}%`);
        bar.style.setProperty('--x', `${visible < 1 ? (progress * (1 - visible) * 100) / visible : 0}%`);
        bar.parentElement.hidden = visible >= 1;
      }
    }

    selectTab(id) {
      this.tabs.forEach((tab) => {
        const active = tab.dataset.ovatTab === id;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active);
        tab.tabIndex = active ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        const active = panel.dataset.ovatPanel === id;
        panel.hidden = !active;
        if (!active) return;
        panel.querySelectorAll('[data-ovat-reveal]').forEach((el) => el.classList.add('is-inview'));
        [...panel.querySelectorAll('.ovat-track > *')].forEach((item, index) => item.style.setProperty('--d', index));
        panel.classList.remove('is-entering');
        void panel.offsetWidth;
        panel.classList.add('is-entering');
        const track = panel.querySelector('.ovat-track');
        if (track) track.scrollLeft = 0;
      });

      const activeTab = this.tabs.find((tab) => tab.dataset.ovatTab === id);
      const viewAll = this.querySelector('[data-ovat-viewall]');
      if (viewAll && activeTab?.dataset.url) viewAll.href = activeTab.dataset.url;

      this.update();
    }
  }

  /* -------------------------------------------------------------------
     Portada con diapositivas  <ovat-slideshow>
     ------------------------------------------------------------------- */
  class OvatSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.ovat-hero__slide')];
      this.dots = [...this.querySelectorAll('.ovat-hero__dot')];
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.style.setProperty('--speed', `${parseFloat(this.dataset.speed) || 6}s`);

      this.querySelectorAll('video').forEach((video) => {
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.play().catch(() => {});
      });

      this.dots.forEach((dot, index) =>
        dot.addEventListener('click', () => {
          this.goTo(index);
        })
      );

      if (this.autoplay) {
        this.dots.forEach((dot) =>
          dot.addEventListener('animationend', () => {
            if (dot.classList.contains('is-active')) this.goTo(this.index + 1);
          })
        );
      } else {
        this.classList.add('is-paused');
      }

      let touchStartX = 0;
      this.addEventListener('touchstart', (event) => (touchStartX = event.touches[0].clientX), { passive: true });
      this.addEventListener('touchend', (event) => {
        const dx = event.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) > 50 && this.slides.length > 1) this.goTo(this.index + (dx < 0 ? 1 : -1));
      });

      if ('IntersectionObserver' in window && this.autoplay) {
        new IntersectionObserver(([entry]) => this.classList.toggle('is-paused', !entry.isIntersecting)).observe(this);
      }

      document.addEventListener('visibilitychange', () => {
        if (this.autoplay) this.classList.toggle('is-paused', document.hidden);
      });

      if (designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          const index = this.slides.indexOf(event.target);
          if (index < 0) return;
          this.goTo(index);
          this.classList.add('is-paused');
        });
        document.addEventListener('shopify:block:deselect', (event) => {
          if (this.slides.includes(event.target) && this.autoplay) this.classList.remove('is-paused');
        });
      }

      const first = this.slides[this.index];
      requestAnimationFrame(() => requestAnimationFrame(() => first?.classList.add('is-inview')));
    }

    goTo(target) {
      const count = this.slides.length;
      if (!count) return;
      const next = (target + count) % count;

      const dot = this.dots[next];
      this.dots.forEach((d) => d.classList.remove('is-active'));
      if (dot) {
        void dot.offsetWidth;
        dot.classList.add('is-active');
        this.dots.forEach((d) => d.setAttribute('aria-current', d === dot));
      }

      if (next === this.index) return;

      const previous = this.slides[this.index];
      const current = this.slides[next];
      previous.classList.remove('is-active');
      previous.setAttribute('aria-hidden', 'true');
      setTimeout(() => {
        if (!previous.classList.contains('is-active')) previous.classList.remove('is-inview');
      }, 1300);

      current.classList.add('is-active');
      current.removeAttribute('aria-hidden');
      requestAnimationFrame(() => requestAnimationFrame(() => current.classList.add('is-inview')));
      this.index = next;
    }
  }

  /* -------------------------------------------------------------------
     Cuenta regresiva  <ovat-timer>
     ------------------------------------------------------------------- */
  class OvatTimer extends HTMLElement {
    connectedCallback() {
      this.end = new Date((this.dataset.end || '').replace(' ', 'T')).getTime();
      if (Number.isNaN(this.end)) return;
      this.units = {
        d: this.querySelector('[data-unit="d"]'),
        h: this.querySelector('[data-unit="h"]'),
        m: this.querySelector('[data-unit="m"]'),
        s: this.querySelector('[data-unit="s"]'),
      };
      this.tick();
      this.interval = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    tick() {
      const diff = Math.max(0, this.end - Date.now());
      this.setUnit('d', Math.floor(diff / 864e5));
      this.setUnit('h', Math.floor(diff / 36e5) % 24);
      this.setUnit('m', Math.floor(diff / 6e4) % 60);
      this.setUnit('s', Math.floor(diff / 1e3) % 60);

      if (diff === 0) {
        clearInterval(this.interval);
        const done = this.parentElement.querySelector('[data-ovat-done]');
        if (done) {
          done.hidden = false;
          this.hidden = true;
        }
      }
    }

    setUnit(key, value) {
      const el = this.units[key];
      if (!el) return;
      const text = String(value).padStart(2, '0');
      if (el.dataset.value === text) return;
      const span = document.createElement('span');
      span.textContent = text;
      if (el.dataset.value !== undefined && !reduceMotion) span.className = 'is-ticking';
      el.dataset.value = text;
      el.replaceChildren(span);
    }
  }

  /* -------------------------------------------------------------------
     Antes / después  <ovat-compare>
     ------------------------------------------------------------------- */
  class OvatCompare extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('input[type="range"]');
      if (!this.range) return;
      this.touched = false;
      this.range.addEventListener('input', () => {
        this.touched = true;
        this.set(this.range.value);
      });
      this.set(this.range.value);

      if (reduceMotion || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          setTimeout(() => this.hint(), 400);
        },
        { threshold: 0.6 }
      );
      observer.observe(this);
    }

    set(value) {
      this.style.setProperty('--pos', `${value}%`);
    }

    hint() {
      const start = parseFloat(this.range.value);
      const points = [start, start - 16, start + 12, start];
      const segment = 650;
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      const begin = performance.now();

      const frame = (now) => {
        if (this.touched) return;
        const elapsed = now - begin;
        const index = Math.min(points.length - 2, Math.floor(elapsed / segment));
        const t = Math.min(1, (elapsed - index * segment) / segment);
        const value = points[index] + (points[index + 1] - points[index]) * ease(t);
        this.set(value);
        if (elapsed < segment * (points.length - 1)) requestAnimationFrame(frame);
        else this.set(start);
      };
      requestAnimationFrame(frame);
    }
  }

  /* -------------------------------------------------------------------
     Compra el look (puntos sobre la imagen)  <ovat-look>
     ------------------------------------------------------------------- */
  class OvatLook extends HTMLElement {
    connectedCallback() {
      this.spots = [...this.querySelectorAll('.ovat-hotspot')];
      this.items = [...this.querySelectorAll('.ovat-look__item')];
      this.grid = this.querySelector('.ovat-look__grid');

      this.spots.forEach((spot) => {
        const button = spot.querySelector('.ovat-hotspot__dot');
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const willOpen = !spot.classList.contains('is-open');
          this.closeAll();
          if (willOpen) this.open(spot);
        });
        if (canHover) {
          spot.addEventListener('mouseenter', () => {
            this.closeAll();
            this.open(spot);
          });
          spot.addEventListener('mouseleave', () => this.closeAll());
        }
      });

      this.items.forEach((item) => {
        item.addEventListener('mouseenter', () => this.focus(item.dataset.index));
        item.addEventListener('mouseleave', () => this.focus(null));
      });

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.closeAll();
      };
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    open(spot) {
      spot.classList.add('is-open');
      spot.querySelector('.ovat-hotspot__dot').setAttribute('aria-expanded', 'true');
      this.focus(spot.dataset.index);
    }

    closeAll() {
      this.spots.forEach((spot) => {
        spot.classList.remove('is-open');
        spot.querySelector('.ovat-hotspot__dot').setAttribute('aria-expanded', 'false');
      });
      this.focus(null);
    }

    focus(index) {
      if (!this.grid) return;
      this.grid.classList.toggle('has-focus', index != null);
      this.items.forEach((item) => item.classList.toggle('is-focus', item.dataset.index === index));
    }
  }

  /* -------------------------------------------------------------------
     Cinta de texto: asegura que cada grupo cubra el ancho de pantalla
     ------------------------------------------------------------------- */
  class OvatMarquee extends HTMLElement {
    connectedCallback() {
      requestAnimationFrame(() => this.fill());
    }

    fill() {
      const groups = this.querySelectorAll('.ovat-marquee__group');
      if (groups.length < 2) return;
      const original = [...groups[0].children];
      if (!original.length) return;
      let guard = 0;
      while (groups[0].scrollWidth < window.innerWidth * 1.1 && guard < 8) {
        groups.forEach((group) => original.forEach((node) => group.appendChild(node.cloneNode(true))));
        guard++;
      }
    }
  }

  /* -------------------------------------------------------------------
     Popup de newsletter  <ovat-popup>
     ------------------------------------------------------------------- */
  class OvatPopup extends HTMLElement {
    connectedCallback() {
      this.key = `ovat-popup-${this.dataset.sectionId || 'newsletter'}`;
      this.onKeyDown = (event) => {
        if (event.key === 'Escape') this.close();
      };

      this.querySelectorAll('[data-ovat-close]').forEach((el) => el.addEventListener('click', () => this.close()));

      if (designMode) {
        document.addEventListener('shopify:section:select', (event) => {
          if (event.target.contains(this)) this.open();
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.target.contains(this)) this.close(false);
        });
        return;
      }

      if (this.querySelector('[data-ovat-posted]')) {
        this.open();
        return;
      }

      let last = 0;
      try {
        last = Number(localStorage.getItem(this.key)) || 0;
      } catch (error) {
        last = 0;
      }
      const days = parseFloat(this.dataset.days || '7');
      if (Date.now() - last < days * 864e5) return;

      setTimeout(() => this.open(), (parseFloat(this.dataset.delay) || 5) * 1000);
    }

    open() {
      if (this.classList.contains('is-open')) return;
      this.previousFocus = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.addEventListener('keydown', this.onKeyDown);
      setTimeout(() => this.querySelector('.ovat-popup__close')?.focus({ preventScroll: true }), 50);
    }

    close(remember = true) {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', this.onKeyDown);
      if (remember && !designMode) {
        try {
          localStorage.setItem(this.key, String(Date.now()));
        } catch (error) {
          /* almacenamiento no disponible */
        }
      }
      this.previousFocus?.focus?.({ preventScroll: true });
    }
  }

  /* -------------------------------------------------------------------
     Registro e inicio
     ------------------------------------------------------------------- */
  const define = (name, element) => {
    if (!customElements.get(name)) customElements.define(name, element);
  };
  define('ovat-slider', OvatSlider);
  define('ovat-slideshow', OvatSlideshow);
  define('ovat-timer', OvatTimer);
  define('ovat-compare', OvatCompare);
  define('ovat-look', OvatLook);
  define('ovat-marquee', OvatMarquee);
  define('ovat-popup', OvatPopup);

  function init(scope) {
    setHeaderHeight();
    collectParallax();
    initReveal(scope);
    onScrollFrame();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  window.addEventListener('load', setHeaderHeight);

  if (designMode) {
    document.addEventListener('shopify:section:load', (event) => init(event.target));
    document.addEventListener('shopify:section:reorder', () => init(document));
  }
})();
